/**
 * The FHIR R4 subset ASSIST stores, as runtime-validated schemas.
 *
 * Every record is a FHIR resource. Only the fields the platform relies on are validated strictly;
 * other valid FHIR fields pass through untouched, so packs can add them without a platform change.
 * Ethiopian naming (first name, father's name, grandfather's name; no family name) is stored as
 * `name[0].given = [first, father, grandfather]` with `text` holding the full name. This will be
 * aligned with a national FHIR implementation guide if one is published [verify].
 */
import { z } from 'zod';
import { parseIsoDate } from './ethiopian.js';
import { isUuid } from './ids.js';
export const ASSIST_FHIR_BASE = 'https://assist.epichealthsystems.org/fhir';
export const EXT_BIRTHDATE_ESTIMATED = `${ASSIST_FHIR_BASE}/StructureDefinition/birthdate-estimated`;
export const SYS_FACILITY_MRN = `${ASSIST_FHIR_BASE}/sid/facility-mrn`;
export const SYS_PROGRAMME = `${ASSIST_FHIR_BASE}/CodeSystem/programme`;
export const SYS_CONCEPT = `${ASSIST_FHIR_BASE}/CodeSystem/concept`;
export const SYS_DRUG = `${ASSIST_FHIR_BASE}/CodeSystem/drug`;
/** Which pack form an Encounter recorded (an ANC contact, a delivery summary...). */
export const SYS_FORM = `${ASSIST_FHIR_BASE}/CodeSystem/form`;
/** A newborn's (or exposed infant's) mother: Patient extension valueReference. */
export const EXT_MOTHER = `${ASSIST_FHIR_BASE}/StructureDefinition/mother`;
export const SYS_LOINC = 'http://loinc.org';
export const SYS_ICD11 = 'http://id.who.int/icd/release/11/mms';
/** WHO ICD-11 entity URIs (http://id.who.int/icd/...), kept with each ICD-11 code. */
export const SYS_ICD11_ENTITY = 'http://id.who.int/icd/entity';
export const EXT_CDS_PROVENANCE = `${ASSIST_FHIR_BASE}/StructureDefinition/cds-provenance`;
export const EXT_NEXT_VISIT = `${ASSIST_FHIR_BASE}/StructureDefinition/next-visit`;
export const SYS_ACT_CODE = 'http://terminology.hl7.org/CodeSystem/v3-ActCode';
export const SYS_TASK = `${ASSIST_FHIR_BASE}/CodeSystem/task`;
export const SYS_TRACING_OUTCOME = `${ASSIST_FHIR_BASE}/CodeSystem/tracing-outcome`;
export const SYS_SNOMED = 'http://snomed.info/sct';
export const EXT_SMS_CONSENT = `${ASSIST_FHIR_BASE}/StructureDefinition/sms-consent`;
export const EXT_FOLLOW_UP_FROM = `${ASSIST_FHIR_BASE}/StructureDefinition/follow-up-from`;
export const EXT_EPISODE_END_REASON = `${ASSIST_FHIR_BASE}/StructureDefinition/episode-end-reason`;
/** A record removed as entered in error: reason, by, at (and an episode's previous status). */
export const EXT_REMOVED = `${ASSIST_FHIR_BASE}/StructureDefinition/removed`;
export const EXT_REFERRAL_OUTCOME = `${ASSIST_FHIR_BASE}/StructureDefinition/referral-outcome`;
export const EXT_REFERRAL_TRANSPORT = `${ASSIST_FHIR_BASE}/StructureDefinition/referral-transport`;
export const EXT_CDS_RULE = `${ASSIST_FHIR_BASE}/StructureDefinition/cds-rule`;
/** FHIR date: YYYY, YYYY-MM or YYYY-MM-DD. */
const FHIR_DATE = /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/;
/** FHIR dateTime. When a time is present, an offset is REQUIRED (no floating clinical times). */
const FHIR_DATETIME = /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01])(T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d{1,9})?(Z|[+-]((0\d|1[0-3]):[0-5]\d|14:00)))?)?)?$/;
export const zId = z.string().refine(isUuid, 'must be a UUID');
/** A full YYYY-MM-DD prefix must be a real calendar day (no 30 February). */
const realDay = (v) => v.length < 10 || parseIsoDate(v.slice(0, 10)) !== null;
export const zDate = z
    .string()
    .regex(FHIR_DATE, 'must be a FHIR date')
    .refine(realDay, 'no such day');
export const zDateTime = z
    .string()
    .regex(FHIR_DATETIME, 'must be a FHIR dateTime with offset')
    .refine(realDay, 'no such day');
const zCode = z.string().min(1).max(128);
export const zCoding = z
    .object({
    system: z.string().url().optional(),
    code: zCode.optional(),
    display: z.string().max(256).optional(),
})
    .passthrough();
export const zCodeableConcept = z
    .object({ coding: z.array(zCoding).max(20).optional(), text: z.string().max(512).optional() })
    .passthrough()
    .refine((c) => (c.coding?.length ?? 0) > 0 || !!c.text, 'needs a coding or text');
const refTo = (type) => z
    .object({
    reference: z
        .string()
        .refine((r) => r.startsWith(`${type}/`) && isUuid(r.slice(type.length + 1)), `must be ${type}/<uuid>`),
    display: z.string().max(256).optional(),
})
    .passthrough();
export const zMeta = z
    .object({
    versionId: z.string().regex(/^\d+$/).optional(),
    lastUpdated: zDateTime.optional(),
    source: z.string().max(256).optional(),
})
    .passthrough();
const base = (resourceType) => ({
    resourceType: z.literal(resourceType),
    id: zId,
    meta: zMeta.optional(),
});
export const zPatient = z
    .object({
    ...base('Patient'),
    active: z.boolean().optional(),
    identifier: z
        .array(z.object({ system: z.string().url(), value: z.string().min(1).max(64) }).passthrough())
        .max(10)
        .optional(),
    name: z
        .array(z
        .object({
        use: z
            .enum(['usual', 'official', 'temp', 'nickname', 'anonymous', 'old', 'maiden'])
            .optional(),
        text: z.string().max(256).optional(),
        given: z.array(z.string().min(1).max(80)).max(4).optional(),
        family: z.string().max(80).optional(),
    })
        .passthrough())
        .min(1)
        .max(5),
    gender: z.enum(['male', 'female', 'other', 'unknown']),
    birthDate: zDate.optional(),
    telecom: z
        .array(z
        .object({ system: z.enum(['phone', 'sms', 'email', 'other']), value: z.string().max(64) })
        .passthrough())
        .max(5)
        .optional(),
    deceasedBoolean: z.boolean().optional(),
    deceasedDateTime: zDateTime.or(zDate).optional(),
    address: z
        .array(z
        .object({
        text: z.string().max(256).optional(),
        line: z.array(z.string().max(120)).max(4).optional(),
        district: z.string().max(120).optional(),
        state: z.string().max(120).optional(),
    })
        .passthrough())
        .max(3)
        .optional(),
})
    .passthrough();
export const zEpisodeOfCare = z
    .object({
    ...base('EpisodeOfCare'),
    status: z.enum([
        'planned',
        'waitlist',
        'active',
        'onhold',
        'finished',
        'cancelled',
        'entered-in-error',
    ]),
    type: z.array(zCodeableConcept).min(1).max(5),
    patient: refTo('Patient'),
    period: z
        .object({ start: zDateTime.or(zDate), end: zDateTime.or(zDate).optional() })
        .passthrough()
        .optional(),
})
    .passthrough();
export const zEncounter = z
    .object({
    ...base('Encounter'),
    status: z.enum([
        'planned',
        'arrived',
        'triaged',
        'in-progress',
        'onleave',
        'finished',
        'cancelled',
        'entered-in-error',
        'unknown',
    ]),
    class: zCoding,
    type: z.array(zCodeableConcept).max(5).optional(),
    subject: refTo('Patient'),
    episodeOfCare: z.array(refTo('EpisodeOfCare')).max(5).optional(),
    period: z.object({ start: zDateTime, end: zDateTime.optional() }).passthrough(),
})
    .passthrough();
const zQuantity = z
    .object({
    value: z.number().finite(),
    unit: z.string().max(32).optional(),
    system: z.string().url().optional(),
    code: z.string().max(32).optional(),
})
    .passthrough();
export const zObservation = z
    .object({
    ...base('Observation'),
    status: z.enum([
        'registered',
        'preliminary',
        'final',
        'amended',
        'corrected',
        'cancelled',
        'entered-in-error',
        'unknown',
    ]),
    code: zCodeableConcept,
    subject: refTo('Patient'),
    encounter: refTo('Encounter').optional(),
    effectiveDateTime: zDateTime,
    valueQuantity: zQuantity.optional(),
    valueCodeableConcept: zCodeableConcept.optional(),
    valueString: z.string().max(2000).optional(),
    valueBoolean: z.boolean().optional(),
    valueInteger: z.number().int().optional(),
    valueDateTime: zDateTime.optional(),
    component: z
        .array(z.object({ code: zCodeableConcept, valueQuantity: zQuantity.optional() }).passthrough())
        .max(20)
        .optional(),
})
    .passthrough();
export const zCondition = z
    .object({
    ...base('Condition'),
    clinicalStatus: zCodeableConcept,
    verificationStatus: zCodeableConcept.optional(),
    code: zCodeableConcept,
    subject: refTo('Patient'),
    onsetDateTime: zDateTime.optional(),
    recordedDate: zDateTime.optional(),
})
    .passthrough();
export const zMedicationRequest = z
    .object({
    ...base('MedicationRequest'),
    status: z.enum([
        'active',
        'on-hold',
        'cancelled',
        'completed',
        'entered-in-error',
        'stopped',
        'draft',
        'unknown',
    ]),
    intent: z.enum([
        'proposal',
        'plan',
        'order',
        'original-order',
        'reflex-order',
        'filler-order',
        'instance-order',
        'option',
    ]),
    medicationCodeableConcept: zCodeableConcept,
    subject: refTo('Patient'),
    encounter: refTo('Encounter').optional(),
    authoredOn: zDateTime,
    dosageInstruction: z
        .array(z
        .object({
        text: z.string().max(500).optional(),
        timing: z
            .object({
            repeat: z
                .object({
                frequency: z.number().int().positive().max(24).optional(),
                period: z.number().positive().optional(),
                periodUnit: z.string().max(8).optional(),
            })
                .passthrough()
                .optional(),
        })
            .passthrough()
            .optional(),
        doseAndRate: z
            .array(z.object({ doseQuantity: zQuantity.optional() }).passthrough())
            .max(3)
            .optional(),
    })
        .passthrough())
        .max(5)
        .optional(),
})
    .passthrough();
const zAnnotation = z
    .object({ text: z.string().min(1).max(2000), time: zDateTime.optional() })
    .passthrough();
/** A follow-up action on a patient: here, one attempt to trace a patient who missed a visit. */
export const zTask = z
    .object({
    ...base('Task'),
    status: z.enum([
        'draft',
        'requested',
        'received',
        'accepted',
        'rejected',
        'ready',
        'cancelled',
        'in-progress',
        'on-hold',
        'failed',
        'completed',
        'entered-in-error',
    ]),
    intent: z.enum([
        'unknown',
        'proposal',
        'plan',
        'order',
        'original-order',
        'reflex-order',
        'filler-order',
        'instance-order',
        'option',
    ]),
    code: zCodeableConcept,
    for: refTo('Patient'),
    authoredOn: zDateTime,
    businessStatus: zCodeableConcept.optional(),
    note: z.array(zAnnotation).max(10).optional(),
})
    .passthrough();
/** A referral to another facility or service, open until its outcome is recorded. */
export const zServiceRequest = z
    .object({
    ...base('ServiceRequest'),
    status: z.enum([
        'draft',
        'active',
        'on-hold',
        'revoked',
        'completed',
        'entered-in-error',
        'unknown',
    ]),
    intent: z.enum([
        'proposal',
        'plan',
        'directive',
        'order',
        'original-order',
        'reflex-order',
        'filler-order',
        'instance-order',
        'option',
    ]),
    category: z.array(zCodeableConcept).max(5).optional(),
    priority: z.enum(['routine', 'urgent', 'asap', 'stat']).optional(),
    code: zCodeableConcept,
    subject: refTo('Patient'),
    encounter: refTo('Encounter').optional(),
    authoredOn: zDateTime,
    reasonCode: z.array(zCodeableConcept).max(5).optional(),
    note: z.array(zAnnotation).max(10).optional(),
})
    .passthrough();
export const RESOURCE_SCHEMAS = {
    Patient: zPatient,
    EpisodeOfCare: zEpisodeOfCare,
    Encounter: zEncounter,
    Observation: zObservation,
    Condition: zCondition,
    MedicationRequest: zMedicationRequest,
    Task: zTask,
    ServiceRequest: zServiceRequest,
};
export const RESOURCE_TYPES = Object.keys(RESOURCE_SCHEMAS);
export function isResourceType(t) {
    return typeof t === 'string' && t in RESOURCE_SCHEMAS;
}
/** Largest resource the platform accepts, serialised (bytes of UTF-16 code units, close enough). */
export const MAX_RESOURCE_CHARS = 64_000;
/** Validate an unknown value as one of the supported resources. Never throws. */
export function validateResource(value) {
    if (typeof value !== 'object' || value === null)
        return { ok: false, error: 'not an object' };
    const rt = value.resourceType;
    if (!isResourceType(rt))
        return { ok: false, error: `unsupported resourceType: ${String(rt)}` };
    if (JSON.stringify(value).length > MAX_RESOURCE_CHARS)
        return { ok: false, error: 'resource too large' };
    const r = RESOURCE_SCHEMAS[rt].safeParse(value);
    if (!r.success) {
        const first = r.error.issues[0];
        return {
            ok: false,
            error: `${rt}.${first?.path.join('.') ?? ''}: ${first?.message ?? 'invalid'}`,
        };
    }
    return { ok: true, resource: r.data };
}
/** The Patient a resource belongs to (its own id for a Patient). */
export function patientIdOf(r) {
    switch (r.resourceType) {
        case 'Patient':
            return r.id;
        case 'EpisodeOfCare':
            return r.patient.reference.slice('Patient/'.length);
        case 'Encounter':
        case 'Observation':
        case 'Condition':
        case 'MedicationRequest':
        case 'ServiceRequest':
            return r.subject.reference.slice('Patient/'.length);
        case 'Task':
            return r.for.reference.slice('Patient/'.length);
    }
}
/** References a resource makes to other records (used to check the target exists). */
export function referencesOf(r) {
    switch (r.resourceType) {
        case 'Patient':
            return [];
        case 'EpisodeOfCare':
            return [r.patient.reference];
        case 'Encounter':
            return [r.subject.reference, ...(r.episodeOfCare ?? []).map((e) => e.reference)];
        case 'Observation':
        case 'MedicationRequest':
        case 'ServiceRequest':
            return [r.subject.reference, ...(r.encounter ? [r.encounter.reference] : [])];
        case 'Condition':
            return [r.subject.reference];
        case 'Task':
            return [r.for.reference];
    }
}
//# sourceMappingURL=fhir.js.map